import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Navigation } from "./Dashboard";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
  Volume2, 
  ArrowLeft, 
  ArrowRight, 
  RotateCcw,
  CheckCircle,
  XCircle,
  Shuffle
} from "lucide-react";
import AudioButton from "../components/AudioButton";
import FavoriteButton from "../components/FavoriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Learn() {
  const location = useLocation();
  const [flashcardSets, setFlashcardSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [currentWords, setCurrentWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [knownWords, setKnownWords] = useState(new Set());
  const [unknownWords, setUnknownWords] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSets = async () => {
      try {
        const response = await fetch(`${API}/flashcards`);
        if (response.ok) {
          const data = await response.json();
          setFlashcardSets(data);
          
          // If set was passed via navigation state, load it
          if (location.state?.setId) {
            const set = data.find(s => s.set_id === location.state.setId);
            if (set) {
              loadSet(set.set_id);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching flashcard sets:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSets();
  }, [location.state]);

  const loadSet = async (setId) => {
    try {
      const response = await fetch(`${API}/flashcards/${setId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedSet(data.set);
        setCurrentWords(data.words);
        setCurrentIndex(0);
        setIsFlipped(false);
        setKnownWords(new Set());
        setUnknownWords(new Set());
      }
    } catch (error) {
      console.error("Error loading flashcard set:", error);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (currentIndex < currentWords.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleKnown = () => {
    const wordId = currentWords[currentIndex].word_id;
    setKnownWords(prev => new Set([...prev, wordId]));
    unknownWords.delete(wordId);
    handleNext();
  };

  const handleUnknown = () => {
    const wordId = currentWords[currentIndex].word_id;
    setUnknownWords(prev => new Set([...prev, wordId]));
    knownWords.delete(wordId);
    handleNext();
  };

  const handleShuffle = () => {
    const shuffled = [...currentWords].sort(() => Math.random() - 0.5);
    setCurrentWords(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownWords(new Set());
    setUnknownWords(new Set());
  };

  const currentWord = currentWords[currentIndex];
  const progress = currentWords.length > 0 ? ((currentIndex + 1) / currentWords.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="learn-page">
      <Navigation />
      
      <main className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {!selectedSet ? (
            // Set Selection View
            <div className="animate-fade-in">
              <div className="mb-8">
                <h1 className="font-heading text-3xl font-bold mb-2">Flashcards</h1>
                <p className="text-muted-foreground">
                  Choisissez un set pour commencer à apprendre
                </p>
              </div>

              {loading ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-6">
                        <div className="h-6 bg-muted rounded w-3/4 mb-3"></div>
                        <div className="h-4 bg-muted rounded w-full mb-2"></div>
                        <div className="h-4 bg-muted rounded w-1/2"></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {flashcardSets.map((set) => (
                    <Card
                      key={set.set_id}
                      className="hover-lift cursor-pointer border-border/50 hover:border-primary/30 transition-colors"
                      onClick={() => loadSet(set.set_id)}
                      data-testid={`flashcard-set-${set.set_id}`}
                    >
                      <CardContent className="p-6">
                        <h3 className="font-heading text-xl font-semibold mb-2">{set.name}</h3>
                        <p className="text-muted-foreground text-sm mb-4">{set.description}</p>
                        <div className="flex items-center gap-3">
                          <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                            {set.niveau}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {set.words?.length || 0} mots
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Flashcard Learning View
            <div className="animate-fade-in">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setSelectedSet(null)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Retour aux sets</span>
                </button>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={handleShuffle} aria-label="Mélanger">
                    <Shuffle className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={handleRestart} aria-label="Recommencer">
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium">{selectedSet.name}</span>
                  <span className="text-muted-foreground">
                    {currentIndex + 1} / {currentWords.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Stats */}
              <div className="flex justify-center gap-6 mb-8">
                <div className="flex items-center gap-2 text-accent">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{knownWords.size} connus</span>
                </div>
                <div className="flex items-center gap-2 text-destructive">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">{unknownWords.size} à revoir</span>
                </div>
              </div>

              {/* Flashcard */}
              {currentWord && (
                <div className="max-w-lg mx-auto">
                  <div
                    className={`flashcard h-80 cursor-pointer ${isFlipped ? 'flipped' : ''}`}
                    onClick={handleFlip}
                    data-testid="flashcard"
                  >
                    <div className="flashcard-inner">
                      {/* Front */}
                      <Card className="flashcard-front border-border/50">
                        <CardContent className="p-8 h-full flex flex-col items-center justify-center text-center">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide mb-4">
                            Baoulé
                          </span>
                          <h2 className="font-heading text-4xl font-bold mb-4">
                            {currentWord.baoulé}
                          </h2>
                          {currentWord.phonétique && (
                            <p className="font-mono text-muted-foreground mb-4">
                              /{currentWord.phonétique}/
                            </p>
                          )}
                          <div className="flex gap-2">
                            <FavoriteButton 
                              wordId={currentWord.word_id}
                              size="lg"
                            />
                            <AudioButton 
                              text={currentWord.baoulé}
                              phonetic={currentWord.phonétique}
                              size="lg"
                            />
                          </div>
                          <p className="text-sm text-muted-foreground mt-4">
                            Cliquez pour voir la traduction
                          </p>
                        </CardContent>
                      </Card>

                      {/* Back */}
                      <Card className="flashcard-back border-border/50 bg-accent/5">
                        <CardContent className="p-8 h-full flex flex-col items-center justify-center text-center">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide mb-4">
                            Français
                          </span>
                          <h2 className="font-heading text-4xl font-bold mb-4">
                            {currentWord.français}
                          </h2>
                          <span className="px-3 py-1 rounded-full bg-muted text-sm mb-4">
                            {currentWord.catégorie}
                          </span>
                          {currentWord.exemple_baoulé && (
                            <div className="text-sm text-muted-foreground">
                              <p className="italic">"{currentWord.exemple_baoulé}"</p>
                              <p>{currentWord.exemple_français}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex justify-center gap-4 mt-8">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handlePrevious}
                      disabled={currentIndex === 0}
                      className="rounded-full"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Précédent
                    </Button>

                    {isFlipped && (
                      <>
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={handleUnknown}
                          className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          À revoir
                        </Button>
                        <Button
                          size="lg"
                          onClick={handleKnown}
                          className="rounded-full bg-accent hover:bg-accent/90"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Je connais
                        </Button>
                      </>
                    )}

                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleNext}
                      disabled={currentIndex === currentWords.length - 1}
                      className="rounded-full"
                    >
                      Suivant
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>

                  {/* Completion Message */}
                  {currentIndex === currentWords.length - 1 && isFlipped && (
                    <div className="text-center mt-8 p-6 rounded-xl bg-primary/5 animate-scale-in">
                      <h3 className="font-heading text-xl font-semibold mb-2">
                        Bravo ! Set terminé !
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Vous avez maîtrisé {knownWords.size} mots sur {currentWords.length}
                      </p>
                      <div className="flex justify-center gap-4">
                        <Button variant="outline" onClick={handleRestart}>
                          Recommencer
                        </Button>
                        <Button onClick={() => setSelectedSet(null)}>
                          Autre set
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
